import { Delete, Edit, Help, History, PictureAsPdf, Visibility } from "@mui/icons-material";
import { Box, Button, Chip, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import {
  GridPagination,
  useGridApiRef,
} from "@mui/x-data-grid";
import type {
  GridColDef,
  GridRenderEditCellParams,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import "dayjs/locale/es";
import type { Timestamp } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { updateEnvio } from "../../firebase/firestore/envios";
import type { Envio } from "../../firebase/firestore/envios";
import { formatEstadoNombre } from "../../utils/estadosUtils";
import { exportPDFEnvios } from "../../utils/exportTable";
import { useEstados } from "../../utils/estadosUtils";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import { useAuthStore } from "../../zustand/useAuthStore";
import { useDateRangeStore } from "../../zustand/useDateRangeStore";
import { useEnvioStore } from "../../zustand/useEnviosStore";
import { UserRole } from "../../interfaces/UserRole";
import ModalDeleteShipment from "../modals/ModalDeleteShipment";
import ModalFormShipment from "../modals/ModalFormShipment";
import ModalTimeline from "../modals/ModalTimeline";
import type { TimelineItem } from "../timeline/Timeline";
import DataTable from "./DataTable";

// Set Spanish locale
dayjs.locale("es");

// Helper function to format dates as "8 de diciembre a las 10:55pm"
const formatFechaCompleta = (timestamp: Timestamp | null | undefined): string => {
  if (!timestamp) return "-";
  try {
    const fecha = dayjs(timestamp.toDate());
    if (!fecha.isValid()) return "-";
    const dia = fecha.format("D");
    const mes = fecha.format("MMMM");
    const hora = fecha.format("h:mma").toLowerCase();
    return `${dia} de ${mes} a las ${hora}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
};

// Custom edit component for estado that auto-opens the Select dropdown
const EditEstadoCell = ({ estados, ...params }: GridRenderEditCellParams & { estados: Array<{ id: string; nombre: string }> }) => {
  const [open, setOpen] = useState(true); // start opened
  // Evita salir dos veces del modo edicion: onClose tambien se dispara al seleccionar
  const commitedRef = useRef(false);

  const handleChange = (e: { target: { value: unknown } }) => {
    commitedRef.current = true;
    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value: e.target.value,
    });
    // Close and commit immediately
    setOpen(false);
    params.api.stopCellEditMode({ id: params.id, field: params.field });
  };

  // El backdrop del Select se queda con el click de afuera, asi que hay que
  // salir del modo edicion aqui o la celda queda con el dropdown cerrado
  const handleClose = () => {
    setOpen(false);
    if (commitedRef.current) return;
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: true,
    });
  };

  return (
    <Select
      value={params.value}
      onChange={handleChange}
      fullWidth
      size="small"
      autoFocus
      open={open}
      onClose={handleClose}
      onOpen={() => setOpen(true)}
      MenuProps={{
        // Prevent scroll jump
        disableAutoFocusItem: true,
      }}
    >
      {estados.map((estado) => (
        <MenuItem key={estado.id} value={estado.id}>
          {estado.nombre}
        </MenuItem>
      ))}
    </Select>
  );
};

export default function TableShipments() {
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { agencia } = useAgenciaStore();
  const { user } = useAuthStore();
  const estados = useEstados();
  const { fechaInicio, fechaFin } = useDateRangeStore();
  const canDelete = user?.role === UserRole.Admin;
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [isOpenModalTimeline, setIsOpenModalTimeline] = useState(false);
  const [entity, setEntity] = useState<Envio>();
  const [envioToDelete, setEnvioToDelete] = useState<Envio>();
  const [envioForTimeline, setEnvioForTimeline] = useState<Envio>();
  const { allEnvios, resetEnvios, fetchNextPage } = useEnvioStore();

  useEffect(() => {
    resetEnvios();
    fetchNextPage();
  }, [resetEnvios, fetchNextPage]);

  const filteredRows = allEnvios.filter((row) => {
    if (!search.trim()) return true;

    const searchLower = search.toLowerCase();

    // Search in nombre
    if (row.nombre?.toLowerCase().includes(searchLower)) return true;

    // Search in cantidadPaquetes
    if (row.cantidadPaquetes !== undefined && row.cantidadPaquetes !== null) {
      if (String(row.cantidadPaquetes).includes(searchLower)) return true;
    }

    // Search in estado
    if (row.estado?.toLowerCase().includes(searchLower)) return true;

    // Search in fechaDesde
    if (row.fechaDesde) {
      const fechaStr = dayjs(row.fechaDesde.toDate()).format("DD/MM/YYYY");
      if (fechaStr.includes(searchLower)) return true;
    }

    // Search in fechaHasta
    if (row.fechaHasta) {
      const fechaStr = dayjs(row.fechaHasta.toDate()).format("DD/MM/YYYY");
      if (fechaStr.includes(searchLower)) return true;
    }

    return false;
  });

  const processRowUpdate = async (newRow: Envio, oldRow: Envio) => {
    try {
      if (newRow.estado !== oldRow.estado) {
        await updateEnvio(newRow.id, { estado: newRow.estado }, oldRow);
        toast.success("Estado del envío y paquetes actualizados con éxito");
      }
      return newRow;
    } catch (error) {
      console.error("Error actualizando envío:", error);
      toast.error("Error al actualizar el envío");
      return oldRow;
    }
  };

  const handleProcessRowUpdateError = (error: unknown) => {
    console.error("Error en processRowUpdate:", error);
    toast.error("Error al actualizar el envío");
  };

  const columns: GridColDef[] = [
    {
      field: "acciones",
      headerName: "Acciones",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", gap: 1 }}>
          <Tooltip title="Ver historial">
            <Button
              variant="outlined"
              sx={{ minWidth: "auto", padding: "5px" }}
              onClick={() => {
                setEnvioForTimeline(params.row);
                setIsOpenModalTimeline(true);
              }}
            >
              <History sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              variant="outlined"
              sx={{ minWidth: "auto", padding: "5px" }}
              onClick={() => {
                setEntity(params.row);
                setIsOpenModal(true);
              }}
            >
              <Edit sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          {canDelete && (
            <Tooltip title="Eliminar">
              <Button
                variant="outlined"
                color="error"
                sx={{ minWidth: "auto", padding: "5px" }}
                onClick={() => {
                  setEnvioToDelete(params.row);
                  setIsOpenModalDelete(true);
                }}
              >
                <Delete sx={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "nombre",
      headerName: "Nombre del envío",
      width: 250,
      sortable: true,
    },
    {
      field: "cantidadPaquetes",
      headerName: "Cantidad de paquetes",
      width: 180,
      sortable: true,
      type: "number",
      renderCell: (params) => {
        const nombreEnvio = params.row.nombre;
        const fechaDesde = params.row.fechaDesde
          ? dayjs(params.row.fechaDesde.toDate()).format("YYYY-MM-DD")
          : "";
        const fechaHasta = params.row.fechaHasta
          ? dayjs(params.row.fechaHasta.toDate()).format("YYYY-MM-DD")
          : "";

        const queryParams = new URLSearchParams();
        queryParams.set("envio", nombreEnvio);
        if (fechaDesde) queryParams.set("fechaDesde", fechaDesde);
        if (fechaHasta) queryParams.set("fechaHasta", fechaHasta);

        return (
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "flex-start",
            gap: 1, 
            width: "100%",
            height: "100%"
          }}>
            <Typography>{params.value}</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/?${queryParams.toString()}`);
              }}
              sx={{ 
                minWidth: "auto", 
                width: 32, 
                height: 32, 
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              title="Ver paquetes"
            >
              <Visibility fontSize="small" />
            </Button>
          </Box>
        );
      },
    },
    {
      field: "corte",
      headerName: "Corte",
      width: 300,
      sortable: false,
      valueGetter: (_value, row) => {
        const desde = row.fechaDesde
          ? dayjs(row.fechaDesde.toDate()).format("DD/MM/YYYY")
          : "-";
        const hasta = row.fechaHasta
          ? dayjs(row.fechaHasta.toDate()).format("DD/MM/YYYY")
          : "-";
        return `${desde} - ${hasta}`;
      },
      renderCell: (params) => {
        const desde = params.row.fechaDesde
          ? dayjs(params.row.fechaDesde.toDate()).format("DD/MM/YYYY")
          : "-";
        const hasta = params.row.fechaHasta
          ? dayjs(params.row.fechaHasta.toDate()).format("DD/MM/YYYY")
          : "-";
        return `${desde} - ${hasta}`;
      },
    },
    {
      field: "fechaCreacion",
      headerName: "Fecha de creación",
      width: 220,
      sortable: true,
      valueGetter: (_value, row) => {
        return row.createdAt ? row.createdAt.toMillis() : 0;
      },
      sortComparator: (v1, v2) => v1 - v2,
      renderCell: (params) => {
        return formatFechaCompleta(params.row.createdAt);
      },
    },
    {
      field: "ultimaActualizacion",
      headerName: "Última actualización",
      width: 220,
      sortable: true,
      valueGetter: (_value, row) => {
        return row.updatedAt ? row.updatedAt.toMillis() : 0;
      },
      sortComparator: (v1, v2) => v1 - v2,
      renderCell: (params) => {
        return formatFechaCompleta(params.row.updatedAt);
      },
    },
    {
      field: "estado",
      renderHeader() {
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Estado
            <Tooltip title="Presione una vez en el estado para modificarlo. Los cambios se aplicarán masivamente a todos los paquetes del envío.">
              <Help fontSize="small" />
            </Tooltip>
          </Box>
        );
      },
      headerName: "Estado",
      width: 180,
      editable: true,
      sortable: true,
      renderEditCell: (params: GridRenderEditCellParams) => {
        return <EditEstadoCell {...params} estados={estados} />;
      },
      renderCell: (params) => {
        const estadoId = params.value as string;
        const estado = estados.find((e) => e.id === estadoId);
        const estadoText = estado?.nombre || formatEstadoNombre(estadoId);
        const color = estado?.colores.light.aereo.a || "#939c9fab";
        return (
          <Chip
            label={estadoText}
            sx={{
              backgroundColor: color,
              color: "#fff",
              textTransform: "capitalize",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onClick={() => {
              if (!apiRef.current) return;
              apiRef.current.startCellEditMode({
                id: params.id,
                field: params.field,
              });
            }}
          />
        );
      },
    },
  ];

  const handleCloseModal = () => {
    setIsOpenModal(false);
    setEntity(undefined);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenModalDelete(false);
    setEnvioToDelete(undefined);
  };

  const handleCloseTimelineModal = () => {
    setIsOpenModalTimeline(false);
    setEnvioForTimeline(undefined);
  };

  // Convertir historialEstados a TimelineItem
  const getTimelineItems = (envio: Envio | undefined): TimelineItem[] => {
    if (!envio || !envio.historialEstados || envio.historialEstados.length === 0) {
      return [];
    }
    return envio.historialEstados.map((item) => ({
      estado: item.estado as TimelineItem["estado"],
      fecha: item.fecha,
      hora: item.hora,
    }));
  };

  return (
    <>
      {entity && (
        <ModalFormShipment
          isOpen={isOpenModal}
          onClose={handleCloseModal}
          entity={entity}
        />
      )}
      {envioToDelete && (
        <ModalDeleteShipment
          isOpen={isOpenModalDelete}
          onClose={handleCloseDeleteModal}
          envio={envioToDelete}
        />
      )}
      {envioForTimeline && (
        <ModalTimeline
          isOpen={isOpenModalTimeline}
          onClose={handleCloseTimelineModal}
          items={getTimelineItems(envioForTimeline)}
          entityName={envioForTimeline.nombre}
          title="Historial de cambios de estado"
        />
      )}
      <Paper sx={{ padding: 2, width: "auto", marginTop: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          marginBottom: 2,
          alignItems: "center",
        }}
      >
        <TextField
          label="Buscar envíos"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Button
          size="small"
          disabled={!filteredRows.length}
          onClick={() => {
            const fechaInicioFormateada = fechaInicio.format("DD/MM/YYYY");
            const fechaFinFormateada = fechaFin.format("DD/MM/YYYY");
            const fileName = `envios-${fechaInicioFormateada}-${fechaFinFormateada}.pdf`;
            exportPDFEnvios(
              fileName,
              filteredRows,
              agencia?.nombre ?? "",
              fechaInicioFormateada + " a " + fechaFinFormateada
            );
          }}
        >
          <Tooltip title="Exportar como archivo PDF">
            <PictureAsPdf sx={{ width: '1.2em', height: '1.2em' }} />
          </Tooltip>
        </Button>
      </Box>

      <DataTable<Envio>
        apiRef={apiRef}
        rows={filteredRows.slice(
          paginationModel.page * paginationModel.pageSize,
          (paginationModel.page + 1) * paginationModel.pageSize
        )}
        columns={columns}
        paginationModel={paginationModel}
        checkboxSelection={false}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        onSelectionModelChange={() => {}}
        onRowDoubleClick={(params) => {
          setEntity(params.row);
          setIsOpenModal(true);
        }}
        slots={{
          footer: () => <GridPagination />,
        }}
        onPaginationModelChange={async (newModel: {
          page: number;
          pageSize: number;
        }) => {
          setPaginationModel(newModel);

          const totalLoaded = allEnvios.length;
          const totalNeeded = (newModel.page + 1) * newModel.pageSize;

          if (totalNeeded > totalLoaded) {
            await fetchNextPage();
          }
        }}
        rowCount={filteredRows.length}
      />
      </Paper>
    </>
  );
}
